import { SortDirection } from "@tanstack/react-table";
import { differenceInMinutes, isSameDay, parseISO, setSeconds } from "date-fns";
import { Timezone } from "../../components-v2/common/layout/Layout";
import { FlightCardTypes } from "../../enum/flights/FlightEnum";
import { FlightTable, TimeMode } from "../../enum/flights/FlightTableEnum";
import { FlightLegTypeEnum } from "../../enum/FlightsEnum";
import { IFlightDetails } from "../../models/flights/IFlightsCommon";
import {
  capitalizeValue,
  getAirportConfig,
} from "../common/CommonHelperFunctions";
import { getConvertedTime } from "../common/DateHelperFunctions";
import {
  additionalDelayKPIColumns,
  otpKPIColumns,
  slotAdherenceKPIColumns,
  turnaroundSufficiencyKPIColumns,
} from "./flight-table/FlightTableConstants";

/**
 * Maps flight details data to an array of objects with title and value properties.
 *
 * @param {IFlightDetails} [flightDetailsData] - The flight details data to be mapped.
 * @returns {Array<{ title: string, value: string | number | null }>} An array of objects containing the mapped flight details.
 */

export function getMappedDetails(flightDetailsData?: IFlightDetails) {
  if (!flightDetailsData) {
    return [];
  }

  const status =
    flightDetailsData.flyingStatus === "unknown" ||
    !flightDetailsData.flyingStatus
      ? "N/A"
      : capitalizeValue(flightDetailsData.flyingStatus);

  const detailsMapping = [
    { title: "Status", value: status },
    { title: "Terminal", value: flightDetailsData.terminal },
    { title: "PAX", value: flightDetailsData.bookedPax },
    { title: "Gate", value: flightDetailsData.gate },
    { title: "Check in counter", value: flightDetailsData.checkInCounter },
    { title: "Baggage belt", value: flightDetailsData.baggageBelt },
    { title: "Baggage chute", value: flightDetailsData.baggageChute },
    { title: "Altitude", value: flightDetailsData.altitude },
    { title: "Latitude", value: flightDetailsData.latitude },
    { title: "Longitude", value: flightDetailsData.longitude },
    { title: "Speed", value: flightDetailsData.speed },
    { title: "Average flight time", value: flightDetailsData.duration },
    { title: "Heading direction", value: flightDetailsData.direction },
    {
      title: "Vertical rate of ascent",
      value: flightDetailsData.verticalRate,
    },
    { title: "Squawk", value: flightDetailsData.squawk },
  ];

  return detailsMapping.map((detail) => ({
    title: detail.title,
    value: detail?.value ?? "N/A",
  }));
}

/**
 * Filters flight data based on the specified leg type.
 *
 * @param flighData - An array of flight data objects to be filtered.
 * @param legType - The type of flight leg to filter by (All, Arrival or Departure).
 * @returns A filtered array of flight data objects based on the specified leg type.
 *
 * @example
 * ```typescript
 * const flights = [
 *   { type: 'a', ... }, // arrival
 *   { type: 'd', ... }, // departure
 *   { type: 't', ... }  // turnaround
 * ];
 * const filteredArrivals = getfilteredData(flights, FlightLegTypeEnum.Arrival);
 * // filteredArrivals will contain flights with type 'a' or 't' (arrival or turnaround flights)
 * ```
 */
export const getfilteredData = (
  flighData: FlightTable[] | any[],
  legType: FlightLegTypeEnum
) => {
  switch (legType) {
    case FlightLegTypeEnum.All:
      return flighData;
    case FlightLegTypeEnum.Turnaround:
      return flighData;
    case FlightLegTypeEnum.Arrival:
      return flighData.filter(
        (flight: FlightTable) => flight.type === "a" || flight.type === "t"
      );
    case FlightLegTypeEnum.Departure:
      return flighData
        .filter(
          (flight: FlightTable) => flight.type === "d" || flight.type === "t"
        )
        .sort((a, b) => {
          const aTime =
            a?.departure?.aobt || a?.departure?.eobt || a?.departure?.sobt;
          const bTime =
            b?.departure?.aobt || b?.departure?.eobt || b?.departure?.sobt;
          return aTime && bTime
            ? new Date(aTime).getTime() - new Date(bTime).getTime()
            : 0;
        });

    default:
      return flighData;
  }
};

/**
 * Calculates the count of all flights, arrival flights, and departure flights from the given flight data.
 *
 * @param {FlightTable[]} flighData - An array of flight data objects.
 * @returns {{ turnaround: number; arrival: number; departure: number }} An object containing the counts of turnaround flights, arrival flights, and departure flights.
 */
export const getFlightLegTypeCount = (
  flighData: any[]
): { turnaround: number; arrival: number; departure: number } => {
  const { arrival, departure, turnaround } = flighData.reduce(
    (acc, flight: FlightTable) => {
      if (flight.type === "a") {
        acc.arrival++;
      }
      if (flight.type === "d") {
        acc.departure++;
      }
      if (flight.type === "t") {
        acc.turnaround++;
      }
      return acc;
    },
    {
      arrival: 0,
      departure: 0,
      turnaround: 0,
    }
  );

  return { arrival, departure, turnaround };
};

/**
 * Transforms an array of column objects based on the specified flight leg type and view type.
 *
 * @param columns - An array of column objects to be transformed. Each column object must have an `id` property
 *                  in the format `columnType.columnId.isKpiColumn`.
 * @param legType - The flight leg type, which determines whether the column is enabled or disabled.
 *                  It can be one of the values from the `FlightLegTypeEnum` (e.g., Arrival, Departure).
 * @param viewType - The flight card view type, used to determine if a KPI column is selected for the current view.
 *                   It can be one of the values from the `FlightCardTypes`.
 * @returns An array of transformed column objects, where each object includes:
 *          - `disabled`: A boolean indicating whether the column is disabled based on the leg type.
 *          - `id`: The original column ID.
 *          - `label`: A transformed label derived from the column type and ID.
 *          - `checked`: A boolean indicating whether the column is selected or not.
 */
export const transformColumns = (
  columns: any[],
  legType: FlightLegTypeEnum,
  viewType: FlightCardTypes
) => {
  return columns.map((column) => {
    const [columnType, columnId, isKpiColumn] = column.id.split(".");
    const transformedName = `${columnType
      .charAt(0)
      .toUpperCase()}${columnType.slice(1)} ${columnId
      .charAt(0)
      .toUpperCase()}${columnId.slice(1)}`;

    const isArrival =
      columnType === FlightLegTypeEnum.Arrival || columnType === "common";
    const isDeparture =
      columnType === FlightLegTypeEnum.Departure || columnType === "common";

    let transformColumn = {};

    switch (legType) {
      case FlightLegTypeEnum.Arrival:
        transformColumn = {
          disabled: !isArrival,
          id: column.id,
          label: transformedName,
          checked: isArrival,
        };
        break;
      case FlightLegTypeEnum.Departure:
        transformColumn = {
          disabled: !isDeparture,
          id: column.id,
          label: transformedName,
          checked: isDeparture,
        };
        break;
      default:
        transformColumn = {
          disabled: false,
          id: column.id,
          label: transformedName,
          checked: true,
        };
    }

    const isSelectedViewKpiColumn = getKPIColumns(viewType).includes(column.id);
    if (isKpiColumn) {
      transformColumn = {
        ...transformColumn,
        checked: isSelectedViewKpiColumn ? true : false,
      };
    }
    return transformColumn;
  });
};

/**
 * Retrieves the KPI (Key Performance Indicator) columns based on the specified flight card view type.
 *
 * @param viewType - The type of flight card view for which KPI columns are required.
 *                    It can be one of the following:
 *                    - `FlightCardTypes.OTP`: On-Time Performance KPI columns.
 *                    - `FlightCardTypes.SLOT_ADHERENCE`: Slot Adherence KPI columns.
 *                    - `FlightCardTypes.TURNAROUND_SUFFICIENCY`: Turnaround Sufficiency KPI columns.
 * @returns An array of KPI columns corresponding to the provided view type.
 *          Returns an empty array if the view type does not match any predefined types.
 */
export const getKPIColumns = (viewType: FlightCardTypes) => {
  switch (viewType) {
    case FlightCardTypes.OTP:
      return otpKPIColumns;
    case FlightCardTypes.SLOT_ADHERENCE:
      return slotAdherenceKPIColumns;
    case FlightCardTypes.TURNAROUND_SUFFICIENCY:
      return turnaroundSufficiencyKPIColumns;
    case FlightCardTypes.ADDITIONAL_DELAY:
      return additionalDelayKPIColumns;
    default:
      return [];
  }
};

/**
 * Determines the sort title based on the sorting capability and the next sort direction.
 *
 * @param canSort - A boolean indicating whether sorting is enabled.
 * @param nextSort - The next sort direction, which can be "asc" (ascending), "desc" (descending), or `false` (no sort).
 * @returns A string representing the sort title ("Sort ascending", "Sort descending", or "Clear sort") if sorting is   enabled, or `undefined` if sorting is not enabled.
 */
export const getSortTitle = (
  canSort: boolean,
  nextSort: SortDirection | false
) =>
  canSort
    ? nextSort === "asc"
      ? "Sort ascending"
      : nextSort === "desc"
        ? "Sort descending"
        : "Clear sort"
    : undefined;

/**
 * Extracts the flight time based on the provided flight data and time mode.
 *
 * @param flightData - The flight data object containing information about the flight.
 * @param mode - The time mode to determine which time to extract.
 *               Use "Best" to prioritize actual or estimated times, or fallback to scheduled times.
 * @returns The extracted flight time as a string, or `null` if the flight data is invalid or unavailable.
 *
 * The function determines the flight time based on the type of flight:
 * - For arrival flights (`type === "t"` or `type === "a"`), it prioritizes AIBT, EIBT, or SIBT based on the mode.
 * - For departure flights (`type === "d"`), it prioritizes AOBT, EOBT, or SOBT based on the mode.
 */
export const extractFlightTime = (
  flightData: FlightTable,
  mode: TimeMode,
  legType: FlightLegTypeEnum
) => {
  if (!flightData) {
    return null;
  }
  let flightTime: string | undefined | null = null;
  if (
    (legType !== FlightLegTypeEnum.Departure && flightData?.type === "t") ||
    flightData?.type === "a"
  ) {
    flightTime =
      mode === "Best"
        ? flightData?.arrival?.aibt ||
          flightData?.arrival?.eibt ||
          flightData?.arrival?.sibt
        : flightData?.arrival?.sibt;
  } else if (
    (legType === FlightLegTypeEnum.Departure && flightData?.type === "t") ||
    flightData?.type === "d"
  ) {
    flightTime =
      mode === "Best"
        ? flightData?.departure?.aobt ||
          flightData?.departure?.eobt ||
          flightData?.departure?.sobt
        : flightData?.departure?.sobt;
  }
  return flightTime;
};

/**
 * Finds the index of the flight in the provided flight data array that is closest to the current time
 * for a given airport and time mode. The function uses binary search to efficiently locate the nearest flight.
 *
 * @param flightsData - An array of flight data objects.
 * @param airportCode - The airport code used to determine the time zone.
 * @param timeMode - The mode of time to compare (e.g., departure or arrival time).
 * @returns The index of the flight closest to the current time, or -1 if no valid flight is found.
 *
 * @remarks
 * - The function assumes that the `flightsData` array is sorted by time in ascending order.
 * - If the time of a flight is not on the same day as the current time, it is skipped.
 * - If no exact match is found, the index of the nearest flight is returned.
 * - If `timeToCompare` is null or undefined for a flight, the function returns -1.
 */
export function findCurrentTimeFlightIndex(
  flightsData: any[],
  airportCode: string,
  timeMode: TimeMode,
  legType: FlightLegTypeEnum,
  timezone: Timezone
) {
  const { timeZoneId: airportTimeZone } = getAirportConfig(airportCode);
  const now = setSeconds(
    getConvertedTime(new Date(), timezone, airportTimeZone),
    0
  );

  if (!now) {
    return -1; // Skip if timeToCompare is null or undefined
  }

  let left = 0;
  let right = flightsData.length - 1;
  let nearestIndex = -1;
  let smallestDifference = Infinity;

  // Binary search to find the nearest flight time
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const timeToCompare = extractFlightTime(
      flightsData[mid],
      timeMode,
      legType
    );

    if (!timeToCompare) {
      return -1; // Skip if timeToCompare is null or undefined
    }

    const parsedTime = parseISO(timeToCompare);
    if (!isSameDay(parsedTime, now)) {
      if (parsedTime < now) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
      continue; // Skip if the date is not the same
    }

    const { newSmallestDifference, newNearestIndex } = calculateNearestIndex(
      smallestDifference,
      nearestIndex,
      parsedTime,
      now,
      mid
    );

    smallestDifference = newSmallestDifference;
    nearestIndex = newNearestIndex;

    if (parsedTime < now) {
      left = mid + 1;
    } else if (parsedTime > now) {
      right = mid - 1;
    } else {
      return mid; // Exact match found
    }
  }
  return nearestIndex; // Return the index of the nearest row if no exact match is found
}

export const calculateNearestIndex = (
  smallestDifference: number,
  nearestIndex: number,
  parsedTime: Date,
  now: Date,
  mid: number
) => {
  const difference = Math.abs(differenceInMinutes(parsedTime, now));
  let newSmallestDifference = smallestDifference;
  let newNearestIndex = nearestIndex;
  if (difference < smallestDifference) {
    newSmallestDifference = difference;
    newNearestIndex = mid;
  }

  return { newSmallestDifference, newNearestIndex };
};

/**
 * Determines the carousel index for a flight KPI card based on the selected flight card type.
 *
 * The mapping depends on the cards configured for each airport.
 * - Any other card type defaults to index `0`.
 */
export const getFlightKPICarouselIndex = (
  airport: string,
  selectedCard: FlightCardTypes
): number => {
  //TODO: Update the mapping logic based on the actual cards configured for each airport.
  if (airport === "XYZ") {
    switch (selectedCard) {
      case FlightCardTypes.OTP:
      case FlightCardTypes.SLOT_ADHERENCE:
        return 0;
      case FlightCardTypes.TURNAROUND_SUFFICIENCY:
      case FlightCardTypes.ADDITIONAL_DELAY:
        return 1;
      case FlightCardTypes.REGULATED_FLIGHT:
      case FlightCardTypes.RUNWAY_DELAYS:
        return 2;
      default:
        return 0;
    }
  }

  if (airport === "YQB") {
    switch (selectedCard) {
      case FlightCardTypes.OTP:
      case FlightCardTypes.TURNAROUND_SUFFICIENCY:
        return 0;
      case FlightCardTypes.ADDITIONAL_DELAY:
      case FlightCardTypes.REGULATED_FLIGHT:
        return 1;
      default:
        return 0;
    }
  }

  return 0;
};

/**
 * Determines whether the "Show All" button should be displayed based on the flight card view type.
 *
 * @param viewType - The type of the flight card view. It can be one of the values from the `FlightCardTypes` enum.
 * @returns `true` if the view type is `SUMMARY` or `OTP`, indicating that the "Show All" button should be displayed; otherwise, `false`.
 */
export const displayAllFlightsView = (viewType: FlightCardTypes): boolean => {
  return (
    viewType === FlightCardTypes.SUMMARY || viewType === FlightCardTypes.OTP
  );
};
